<script setup>
import { computed, ref } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import WoocommerceAPI from 'dashboard/api/integrations/woocommerce';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  hookId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const formatDate = dateString => {
  return format(new Date(dateString), 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

const formatStatus = status => {
  return status
    ?.toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
};

const getStatusClass = status => {
  const classes = {
    completed: 'bg-n-teal-5 text-n-teal-12',
    processing: 'bg-n-amber-5 text-n-amber-12',
    on_hold: 'bg-n-solid-3 text-n-slate-12',
    failed: 'bg-n-ruby-5 text-n-ruby-12',
    refunded: 'bg-n-ruby-5 text-n-ruby-12',
    cancelled: 'bg-n-slate-3 text-n-slate-12',
  };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const financialStatus = computed(() => {
  const status = props.order.financial_status;
  if (!status) {
    return '';
  }

  const translatedStatuses = {
    pending: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.PENDING'),
    processing: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.PROCESSING'),
    on_hold: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.ON_HOLD'),
    completed: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.COMPLETED'),
    cancelled: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.CANCELLED'),
    refunded: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.REFUNDED'),
    failed: t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.FAILED'),
  };

  return translatedStatuses[status] || formatStatus(status);
});

const showDetails = ref(false);
const detailsLoading = ref(false);
const detailsError = ref('');
const details = ref(null);

const billingName = computed(() => {
  const firstName = details.value?.billing?.first_name;
  const lastName = details.value?.billing?.last_name;
  return [firstName, lastName].filter(Boolean).join(' ').trim();
});

const fetchDetails = async () => {
  try {
    detailsLoading.value = true;
    detailsError.value = '';
    const response = await WoocommerceAPI.getOrderDetails(
      props.hookId,
      props.order.id
    );
    details.value = response.data.order;
  } catch (error) {
    detailsError.value =
      error.response?.data?.error || 'CONVERSATION_SIDEBAR.WOOCOMMERCE.ERROR';
  } finally {
    detailsLoading.value = false;
  }
};

const toggleDetails = async () => {
  if (showDetails.value) {
    showDetails.value = false;
    return;
  }

  showDetails.value = true;
  if (details.value || detailsLoading.value) {
    return;
  }

  await fetchDetails();
};
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center">
      <div class="font-medium flex">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_ID', { id: order.id })
          }}
          <i class="i-lucide-external-link pl-5" />
        </a>
      </div>
      <div
        :class="getStatusClass(order.financial_status)"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="financialStatus"
      >
        {{ financialStatus }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span class="text-n-slate-11 border-r border-n-weak pr-2">
        {{ formatDate(order.created_at) }}
      </span>
      <span class="text-n-slate-11 pl-2">
        {{ formatCurrency(order.total_price, order.currency) }}
      </span>
    </div>
    <div class="pt-1">
      <button
        type="button"
        class="text-xs text-n-brand hover:underline"
        @click="toggleDetails"
      >
        {{
          showDetails
            ? $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.HIDE_DETAILS')
            : $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.SHOW_DETAILS')
        }}
      </button>
    </div>
    <div v-if="showDetails" class="mt-2 p-2 rounded bg-n-alpha-2 text-xs">
      <div v-if="detailsLoading" class="flex justify-center items-center py-3">
        <Spinner size="20" class="text-n-brand" />
      </div>
      <div v-else-if="detailsError" class="text-n-ruby-11">
        {{ detailsError }}
      </div>
      <div v-else-if="details" class="flex flex-col gap-2">
        <div class="grid grid-cols-2 gap-2">
          <div>
            <div class="text-n-slate-11">
              {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.PAYMENT_METHOD') }}
            </div>
            <span class="text-n-slate-12">
              {{ details.payment_method_title || '--' }}
            </span>
          </div>
          <div>
            <div class="text-n-slate-11">
              {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.TAX') }}
            </div>
            <span class="text-n-slate-12">
              {{ formatCurrency(details.total_tax, details.currency) }}
            </span>
          </div>
          <div>
            <div class="text-n-slate-11">
              {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.SHIPPING') }}
            </div>
            <span class="text-n-slate-12">
              {{ formatCurrency(details.shipping_total, details.currency) }}
            </span>
          </div>
          <div>
            <div class="text-n-slate-11">
              {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.DISCOUNT') }}
            </div>
            <span class="text-n-slate-12">
              {{ formatCurrency(details.discount_total, details.currency) }}
            </span>
          </div>
        </div>
        <div>
          <div class="text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.BILLING') }}
          </div>
          <div class="text-n-slate-12">
            {{ billingName || '--' }}
          </div>
          <div class="text-n-slate-11">
            {{ details.billing?.email || '--' }}
          </div>
        </div>
        <div>
          <div class="text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ITEMS') }}
          </div>
          <div
            v-for="item in details.line_items"
            :key="item.id"
            class="flex justify-between gap-2 py-1 border-b border-n-weak last:border-b-0"
          >
            <div class="text-n-slate-12 flex-1">
              {{ item.name }}
              <span class="text-n-slate-11">
                {{
                  $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ITEM_QUANTITY', {
                    count: item.quantity,
                  })
                }}
              </span>
              <div>
                {{
                  $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.SKU', { sku: item.sku })
                }}
              </div>
            </div>
            <div class="text-n-slate-12">
              {{ formatCurrency(item.total, details.currency) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
